import { Outlet } from 'react-router';

import { tenantInnerAppConfig } from '@/backoffice/app/config/tenant-inner-app-config';
import { BackofficeTenantFragment } from '@/backoffice/types/types.generated';
import { AppConfigProvider } from '@/shared/features/app-config/context/app-config-context';
import { TCustomRouteObject } from '@/shared/types/router.types';

export const tenantRoutes: TCustomRouteObject = {
  path: '/',
  Component: () => <Outlet />,
  handle: {
    crumb: {
      label: 'tenants',
      localized: true,
    },
  },
  children: [
    {
      index: true,
      lazy: async () => {
        const { TenantsListRoute } = await import('./tenants-list');
        return { Component: TenantsListRoute };
      },
      handle: {
        permission: { action: 'backofficeTenant:readTenant' },
      },
    },
    {
      path: ':tenantId',
      Component: () => (
        <AppConfigProvider value={tenantInnerAppConfig}>
          <Outlet />
        </AppConfigProvider>
      ),
      handle: {
        crumb: {
          // No data means the tenant read failed — drop the crumb rather than
          // labelling it with a raw id.
          label: (data?: BackofficeTenantFragment | null) => data?.companyName,
        },
        // Opening a tenant impersonates it — gate on selectTenant for this id.
        permission: {
          action: 'backofficeTenant:selectTenant',
          idParam: 'tenantId',
        },
      },
      // Tab navigation reuses this match; only refetch when the tenant changes,
      // otherwise the crumb keeps showing the previous tenant's name.
      shouldRevalidate: ({ currentParams, nextParams }) =>
        currentParams.tenantId !== nextParams.tenantId,
      loader: async (args) => {
        const { tenantLoader } = await import('./tenant');
        return tenantLoader(args);
      },
      children: [
        {
          index: true,
          lazy: async () => {
            const { TenantRoute } = await import('./tenant');
            return { Component: TenantRoute };
          },
        },
        {
          path: ':tabId',
          lazy: async () => {
            const { TenantRoute } = await import('./tenant');
            return { Component: TenantRoute };
          },
        },
        {
          path: 'create-subscription',
          lazy: async () => {
            const { CreateSubscriptionRoute } =
              await import('./subscription-plan-routes');
            return { Component: CreateSubscriptionRoute };
          },
          handle: {
            crumb: {
              label: 'create_subscription',
              localized: true,
            },
          },
        },
        {
          path: 'switch-subscription-plan',
          lazy: async () => {
            const { SwitchSubscriptionPlanRoute } =
              await import('./subscription-plan-routes');
            return { Component: SwitchSubscriptionPlanRoute };
          },
          handle: {
            crumb: {
              label: 'switch_subscription_plan',
              localized: true,
            },
          },
        },
        {
          path: 'update-subscription',
          lazy: async () => {
            const { UpdateSubscriptionRoute } =
              await import('./subscription-plan-routes');
            return { Component: UpdateSubscriptionRoute };
          },
          handle: {
            crumb: {
              label: 'update_subscription',
              localized: true,
            },
          },
        },
      ],
    },
  ],
};
